import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, Sparkles, X } from "lucide-react";
import { useLang } from "@/i18n";
import { trackEvent, EVENTS } from "@/lib/analytics";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = sessionStorage.getItem("konek_chat_session");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    sessionStorage.setItem("konek_chat_session", id);
  }
  return id;
};

const displayContent = (s) =>
  s.replace(/<lead[\s\S]*$/i, "").replace(/<\/lead>/gi, "").replace(/[<]+\s*$/, "").trimEnd();

export const ChatWidget = () => {
  const { t } = useLang();
  const c = t.chat;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let failed = false;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + ev.delta };
              return copy;
            });
          } else if (ev.error) {
            failed = true;
          }
        }
      }
      if (failed) throw new Error("stream error");
    } catch {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = { role: "assistant", content: c.error };
        }
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed z-40 flex flex-col items-end"
      style={{ right: "16px", bottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="mb-3 flex h-[min(28rem,calc(100dvh-8rem))] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-navy-100 bg-white shadow-2xl"
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between bg-navy-900 px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gold-500 text-navy-900">
                  <Sparkles size={18} aria-hidden />
                </span>
                <div>
                  <p className="font-display font-semibold text-white leading-tight">{c.title}</p>
                  <p className="text-[11px] text-white/60">{c.subtitle}</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label={c.close}
                data-testid="chat-close"
                className="p-1.5 text-white/60 hover:text-white rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFC]" data-testid="chat-messages">
              <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-white border border-navy-100 px-4 py-3 text-sm text-charcoal-700 leading-relaxed">
                {c.welcome}
              </div>
              {messages.length === 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {c.quick.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      data-testid="chat-quick-question"
                      className="rounded-full border border-navy-100 bg-white px-3 py-1.5 text-xs font-semibold text-navy-900 hover:border-gold-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={cn(
                    "max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap",
                    m.role === "user"
                      ? "ml-auto rounded-tr-md bg-navy-900 text-white"
                      : "rounded-tl-md bg-white border border-navy-100 text-charcoal-700"
                  )}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content ? displayContent(m.content) : (
                    <span className="inline-flex gap-1 items-center" aria-label="Thinking">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold-500 animate-bounce" />
                      <span className="h-1.5 w-1.5 rounded-full bg-gold-500 animate-bounce [animation-delay:120ms]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-gold-500 animate-bounce [animation-delay:240ms]" />
                    </span>
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 border-t border-navy-100 bg-white px-3 py-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={c.placeholder}
                aria-label={c.placeholder}
                data-testid="chat-input"
                className="flex-1 rounded-full border border-navy-100 bg-[#FAFAFC] px-4 py-2.5 text-sm text-charcoal-900 placeholder:text-charcoal-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                aria-label={c.send}
                data-testid="chat-send"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold-500 text-navy-900 hover:bg-gold-400 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
              >
                <Send size={17} aria-hidden />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => {
          if (!open) trackEvent(EVENTS.CHAT_OPENED);
          setOpen((v) => !v);
        }}
        aria-label={open ? c.close : c.open}
        title={c.open}
        data-testid="chat-toggle"
        className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-navy-900 text-gold-400 shadow-[0_10px_30px_-8px_rgba(10,25,47,0.6)] hover:bg-navy-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2"
      >
        {open ? <X size={22} aria-hidden /> : <MessageCircle size={22} aria-hidden />}
      </button>
    </div>
  );
};
